import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface Header_v2Props {
  region: 'ru' | 'us';
}

const regionTexts = {
  ru: {
    products: 'Продукция',
    getQuote: 'Получить предложение',
    cuttingThickness: 'Толщина резки',
    fairs: 'Выставки',
  },
  us: {
    products: 'Products',
    getQuote: 'Request Quote',
    cuttingThickness: 'Cutting Thickness',
    fairs: 'Trade Shows',
  },
};

export default function Header_v2({ region }: Header_v2Props) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const menuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const megaMenuContainerRef = useRef<HTMLDivElement>(null);
  const productsButtonRef = useRef<HTMLButtonElement>(null);

  const texts = regionTexts[region];

  const products = region === 'ru' ? [
    {
      slug: 'kapali-kabin',
      name: 'Полностью закрытый волоконный лазер',
      description: 'Закрытые станки с ЧПУ',
      mainImage: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/48db2d4eadb135f04b847468fe9d54fe.jpeg',
    },
    {
      slug: 'gezer-kabin',
      name: 'Закрытый портальный волоконный лазер',
      description: 'Мобильные кабинные системы',
      mainImage: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/87536d91e938e8feb153e654c7ee1710.png',
    },
    {
      slug: 'boru-profil-kesim',
      name: 'Волоконный лазер для труб и профилей',
      description: 'Решения для резки труб и профилей',
      mainImage: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/4e215bb22311963bc7f79b028b56cdde.png',
    }
  ] : [
    {
      slug: 'kapali-kabin',
      name: 'Fully Enclosed Fiber Laser',
      description: 'Enclosed CNC Cutting Machines',
      mainImage: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/48db2d4eadb135f04b847468fe9d54fe.jpeg',
    },
    {
      slug: 'gezer-kabin',
      name: 'Enclosed Gantry Fiber Laser',
      description: 'Mobile Cabin Systems',
      mainImage: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/87536d91e938e8feb153e654c7ee1710.png',
    },
    {
      slug: 'boru-profil-kesim',
      name: 'Tube and Profile Fiber Laser',
      description: 'Tube And Profile Cutting Solutions',
      mainImage: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/4e215bb22311963bc7f79b028b56cdde.png',
    }
  ];

  const navigation = [
    { name: texts.cuttingThickness, href: `/${region}/cutting-thickness`, isScroll: false },
    ...(region !== 'ru' ? [{ name: texts.fairs, href: `/${region}/fairs`, isScroll: false }] : []),
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleLanguageChange = (newRegion: string) => {
    const routes: Record<string, string> = {
      'tr': '/',
      'en': '/en',
      'ru': '/ru',
      'us': '/us'
    };
    
    window.REACT_APP_NAVIGATE(routes[newRegion] || '/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  const scrollToCTA = () => {
    // Her zaman ana sayfaya git
    navigate(`/${region}`);
    
    // Sayfa yüklendikten sonra forma scroll yap
    setTimeout(() => {
      const ctaElement = document.getElementById('cta-section');
      if (ctaElement) {
        const yOffset = -80;
        const y = ctaElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 300);
    
    setMobileMenuOpen(false);
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/${region}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavigationClick = (item: typeof navigation[0]) => {
    navigate(item.href);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 0);
    setMobileMenuOpen(false);
  };

  const handleMouseEnterProducts = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    setProductsMenuOpen(true);
  };

  const handleMouseLeaveProducts = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setProductsMenuOpen(false);
    }, 300);
  };

  const handleProductsButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setProductsMenuOpen(!productsMenuOpen);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setProductsMenuOpen(false);
    }
  };

  useEffect(() => {
    if (!productsMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideMegaMenu = megaMenuContainerRef.current?.contains(target);
      const isInsideButton = productsButtonRef.current?.contains(target);
      
      if (!isInsideMegaMenu && !isInsideButton) {
        setProductsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, { capture: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, { capture: true });
    };
  }, [productsMenuOpen]);

  useEffect(() => {
    setProductsMenuOpen(false);
    setMobileProductsOpen(false);
  }, [location.pathname]);

  const contactInfo = region === 'ru' ? {
    phone: '+7 (495) 777-88-17',
    phoneHref: 'tel:+74957778817',
    email: 'info@ajan.ru'
  } : {
    phone: '470-970-2526',
    phoneHref: 'tel:+14709702526',
    email: 'support@ajanusa.com'
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '100%' }}>
        <div className="flex h-20 items-center justify-between gap-4">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button onClick={handleLogoClick} className="flex items-center cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/5cdb8acd1a926456732ace3d319bea51.png"
                alt="AJAN LASER"
                className="h-10 sm:h-12 w-auto"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden min-[1280px]:flex min-[1280px]:items-center min-[1280px]:gap-5 ml-8">
            <div 
              className="relative"
              onMouseEnter={handleMouseEnterProducts}
              onMouseLeave={handleMouseLeaveProducts}
            >
              <button
                ref={productsButtonRef}
                onClick={handleProductsButtonClick}
                className="text-[22px] font-semibold transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-[#D62828] flex items-center gap-1"
              >
                {texts.products}
                <i className={`ri-arrow-down-s-line text-xl transition-transform ${productsMenuOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {productsMenuOpen && (
                <div 
                  className="fixed left-0 right-0 top-20 pt-4 flex justify-center animate-fadeIn z-50"
                  onClick={handleOverlayClick}
                >
                  <div 
                    ref={megaMenuContainerRef}
                    className="bg-white rounded-2xl shadow-2xl border-t-4 border-[#D62828] p-8 w-full max-w-[1600px] mx-4"
                    onMouseEnter={handleMouseEnterProducts}
                    onMouseLeave={handleMouseLeaveProducts}
                  >
                    <div className="grid grid-cols-3 gap-10">
                      {products.map((product) => (
                        <button
                          key={product.slug}
                          onClick={() => {
                            navigate(`/${region}/products/${product.slug}`);
                            setProductsMenuOpen(false);
                            setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 0);
                          }}
                          className="group relative bg-white rounded-2xl border border-gray-100 hover:border-[#D62828]/30 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden cursor-pointer"
                        >
                          <div className="bg-gray-50 p-6 overflow-hidden">
                            <div className="h-[340px] w-full overflow-hidden rounded-xl">
                              <img
                                src={product.mainImage}
                                alt={product.name}
                                className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                              />
                            </div>
                          </div>
                          <div className="p-6">
                            <h3 className="text-2xl font-bold text-gray-900 text-center mb-3 group-hover:text-[#D62828] transition-colors">
                              {product.name}
                            </h3>
                            <p className="text-base text-gray-600 text-center">
                              {product.description}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigationClick(item)}
                className={`text-[22px] font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  isActive(item.href) ? 'text-[#D62828]' : 'text-gray-700 hover:text-[#D62828]'
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>

          {/* Desktop Action Buttons */}
          <div className="hidden min-[1280px]:flex min-[1280px]:items-center min-[1280px]:gap-2 ml-auto">
            <button
              onClick={scrollToCTA}
              className="px-4 py-2.5 bg-[#D62828] text-white text-[19px] font-medium rounded-lg hover:bg-[#B91C1C] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-text-line mr-1.5"></i>
              {texts.getQuote}
            </button>
            <a
              href={contactInfo.phoneHref}
              className="px-4 py-2.5 bg-gray-900 text-white text-[19px] font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-line mr-1.5"></i>
              {contactInfo.phone}
            </a>
            
            {/* Region Selector - only show for non-ru regions */}
            {region !== 'ru' && (
              <div className="relative group">
                <button className="w-11 h-11 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                  <img 
                    src="https://flagcdn.com/w40/us.png"
                    alt="USA"
                    className="w-7 h-5 rounded"
                  />
                </button>
                
                {/* Dropdown */}
                <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <button
                    onClick={() => handleLanguageChange('tr')}
                    className="w-full px-4 py-2 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                  >
                    <img src="https://flagcdn.com/w40/tr.png" alt="Türkiye" className="w-6 h-4 rounded" />
                    <span className="text-base">Türkiye (TR)</span>
                  </button>
                  <button
                    onClick={() => handleLanguageChange('en')}
                    className="w-full px-4 py-2 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                  >
                    <img src="https://flagcdn.com/w40/gb.png" alt="Global" className="w-6 h-4 rounded" />
                    <span className="text-base">Global (EN)</span>
                  </button>
                  <button
                    onClick={() => handleLanguageChange('us')}
                    className="w-full px-4 py-2 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                  >
                    <img src="https://flagcdn.com/w40/us.png" alt="USA" className="w-6 h-4 rounded" />
                    <span className="text-base">USA (US)</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="min-[1280px]:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="min-[1280px]:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col gap-4">
              <div className="border-b border-gray-200 pb-4">
                <button
                  onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                  className="text-[19px] font-medium text-gray-700 hover:text-[#D62828] transition-colors text-left cursor-pointer w-full flex items-center justify-between"
                >
                  <span>{texts.products}</span>
                  <i className={`ri-arrow-down-s-line text-xl transition-transform ${mobileProductsOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {mobileProductsOpen && (
                  <div className="mt-4 space-y-3 pl-4">
                    {products.map((product) => (
                      <button
                        key={product.slug}
                        onClick={() => {
                          navigate(`/${region}/products/${product.slug}`);
                          setMobileMenuOpen(false);
                          setMobileProductsOpen(false);
                          setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 0);
                        }}
                        className="block bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors cursor-pointer w-full text-left"
                      >
                        <div className="font-semibold text-gray-900 hover:text-[#D62828] mb-1">{product.name}</div>
                        <div className="text-sm text-gray-600">{product.description}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {navigation.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigationClick(item)}
                  className={`text-[19px] font-medium transition-colors text-left cursor-pointer ${
                    isActive(item.href) ? 'text-[#D62828]' : 'text-gray-700 hover:text-[#D62828]'
                  }`}
                >
                  {item.name}
                </button>
              ))}
              
              <button
                onClick={scrollToCTA}
                className="px-6 py-3 bg-[#D62828] text-white text-[19px] font-medium rounded-lg hover:bg-[#B91C1C] transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-text-line mr-2"></i>
                {texts.getQuote}
              </button>
              
              <a
                href={contactInfo.phoneHref}
                className="px-6 py-3 bg-gray-900 text-white text-[19px] font-medium rounded-lg hover:bg-gray-800 transition-colors text-center whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                {contactInfo.phone}
              </a>

              {/* Mobile Region Selector - only show for non-ru regions */}
              {region !== 'ru' && (
                <div className="border-t border-gray-200 pt-4 space-y-2">
                  <button
                    onClick={() => {
                      handleLanguageChange('tr');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 flex items-center gap-3 cursor-pointer"
                  >
                    <img src="https://flagcdn.com/w40/tr.png" alt="Türkiye" className="w-6 h-4 rounded" />
                    <span className="text-base">Türkiye (TR)</span>
                  </button>
                  <button
                    onClick={() => {
                      handleLanguageChange('en');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 flex items-center gap-3 cursor-pointer"
                  >
                    <img src="https://flagcdn.com/w40/gb.png" alt="Global" className="w-6 h-4 rounded" />
                    <span className="text-base">Global (EN)</span>
                  </button>
                  <button
                    onClick={() => {
                      handleLanguageChange('us');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 flex items-center gap-3 cursor-pointer"
                  >
                    <img src="https://flagcdn.com/w40/us.png" alt="USA" className="w-6 h-4 rounded" />
                    <span className="text-base">USA (US)</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
